import { useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, Download, ExternalLink, FileText, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import api from '../lib/api';
import { getDocLabel } from '../lib/docLabels';

/**
 * Universal Document Preview Modal — built on Radix Dialog primitives so it
 * nests correctly inside another Dialog (e.g. the worker-profile dialog).
 * Radix's DismissableLayer stack guarantees:
 *   - Clicks inside the preview don't bubble dismissals to the parent dialog.
 *   - ESC closes the innermost (this preview) first; parent stays open.
 *   - Focus is trapped inside the preview while it's open.
 *
 * Renders only:
 *   - PDF: first page on a <canvas> via PDF.js (CDN). Indicator + 3s timeout.
 *   - Image: direct <img>.
 *   - Video: native <video preload="metadata">.
 */
export default function DocumentPreviewModal({ open, onClose, doc, directBlobFetcher, title }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [kind, setKind] = useState(null); // 'pdf' | 'image' | 'video' | 'unknown'
    const [filename, setFilename] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pdfPageCount, setPdfPageCount] = useState(0);
    const [pdfRenderTimeout, setPdfRenderTimeout] = useState(false);
    const canvasRef = useRef(null);

    // Lazy-load file once modal opens
    useEffect(() => {
        if (!open || !doc) {
            setBlobUrl(null);
            setKind(null);
            setError('');
            setPdfPageCount(0);
            setPdfRenderTimeout(false);
            return;
        }
        let revokedUrl = null;
        let cancelled = false;

        (async () => {
            setLoading(true);
            setError('');
            try {
                const resp = directBlobFetcher
                    ? await directBlobFetcher()
                    : await api.get(`/files/${doc.storage_path}`, { responseType: 'blob' });
                if (cancelled) return;
                const ct = (doc.content_type || resp.data?.type || '').toLowerCase();
                const name = doc.original_filename || `${doc.doc_type || 'document'}`;
                const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
                let detected = 'unknown';
                if (ct.includes('pdf') || ext === 'pdf') detected = 'pdf';
                else if (ct.startsWith('image/') || ['jpg','jpeg','png','webp','gif','heic'].includes(ext)) detected = 'image';
                else if (ct.startsWith('video/') || ['mp4','mov','webm','m4v','ogv'].includes(ext)) detected = 'video';
                const finalType = detected === 'pdf' ? 'application/pdf'
                    : detected === 'image' ? (ext === 'png' ? 'image/png' : ext === 'webp' ? 'image/webp' : 'image/jpeg')
                    : detected === 'video' ? (ext === 'webm' ? 'video/webm' : 'video/mp4')
                    : (ct || 'application/octet-stream');
                const typedBlob = new Blob([resp.data], { type: finalType });
                const url = URL.createObjectURL(typedBlob);
                revokedUrl = url;
                setBlobUrl(url);
                setKind(detected);
                setFilename(name);
            } catch (err) {
                console.error('Preview fetch failed:', err);
                if (!cancelled) setError('Unable to load preview. Try "Open in full" or "Download".');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            if (revokedUrl) {
                try { URL.revokeObjectURL(revokedUrl); } catch { /* ignore */ }
            }
        };
    }, [open, doc, directBlobFetcher]);

    // Render PDF first page when blob ready
    useEffect(() => {
        if (kind !== 'pdf' || !blobUrl || !canvasRef.current) return;
        const lib = window.pdfjsLib;
        if (!lib) {
            setError('PDF preview library not loaded. Use "Open in full".');
            return;
        }
        let cancelled = false;
        const timeoutId = setTimeout(() => {
            if (!cancelled) setPdfRenderTimeout(true);
        }, 3000);

        (async () => {
            try {
                const loadingTask = lib.getDocument({ url: blobUrl });
                const pdf = await loadingTask.promise;
                if (cancelled) return;
                setPdfPageCount(pdf.numPages);
                const page = await pdf.getPage(1);
                if (cancelled) return;
                const canvas = canvasRef.current;
                if (!canvas) return;
                const ctx = canvas.getContext('2d');
                const viewport = page.getViewport({ scale: 1.0 });
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                await page.render({ canvasContext: ctx, viewport }).promise;
                if (!cancelled) {
                    clearTimeout(timeoutId);
                    setPdfRenderTimeout(false);
                }
            } catch (err) {
                console.error('PDF render failed:', err);
                if (!cancelled) {
                    clearTimeout(timeoutId);
                    setError('PDF preview failed. Use "Open in full".');
                }
            }
        })();

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [kind, blobUrl]);

    const handleDownload = () => {
        if (!blobUrl) return;
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename || 'document';
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const handleOpenFull = () => {
        if (!blobUrl) return;
        const a = document.createElement('a');
        a.href = blobUrl;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    if (!doc) return null;

    const headerLabel = title || getDocLabel(doc);

    return (
        <DialogPrimitive.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay
                    className="fixed inset-0 z-[100] bg-black/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                    data-testid="doc-preview-overlay"
                />
                <DialogPrimitive.Content
                    className="fixed left-[50%] top-[50%] z-[101] translate-x-[-50%] translate-y-[-50%] bg-white border-2 border-zinc-900 flex flex-col w-[100vw] sm:w-[min(95vw,960px)] h-[90vh] sm:h-[85vh] focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                    data-testid="doc-preview-modal"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    <DialogPrimitive.Title className="sr-only">{headerLabel}</DialogPrimitive.Title>

                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 flex-shrink-0 gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex items-center gap-1.5 text-zinc-700 hover:text-zinc-900 text-xs font-semibold uppercase tracking-wider flex-shrink-0"
                            data-testid="doc-preview-back"
                            aria-label="Back to profile"
                        >
                            <ArrowLeft className="w-4 h-4" /> Back
                        </button>
                        <div className="flex items-center gap-2 min-w-0 flex-1 justify-center">
                            <FileText className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                            <p className="font-semibold text-sm truncate capitalize" data-testid="doc-preview-title">{headerLabel}</p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-zinc-400 hover:text-zinc-900 p-1 flex-shrink-0"
                            data-testid="doc-preview-close"
                            aria-label="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-auto bg-zinc-50 flex flex-col items-center justify-start p-4" data-testid="doc-preview-body">
                        {loading && (
                            <div className="flex-1 flex flex-col items-center justify-center gap-2">
                                <div className="w-6 h-6 border-2 border-zinc-900 border-t-transparent animate-spin" />
                                <p className="text-xs text-zinc-500">Loading preview...</p>
                            </div>
                        )}
                        {!loading && error && (
                            <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-testid="doc-preview-error">
                                <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                                <p className="text-sm text-zinc-600 mb-3">{error}</p>
                                {blobUrl && (
                                    <Button variant="outline" className="rounded-none" onClick={handleOpenFull}>
                                        <ExternalLink className="w-4 h-4 mr-2" /> Open in full
                                    </Button>
                                )}
                            </div>
                        )}
                        {!loading && !error && blobUrl && kind === 'pdf' && (
                            <div className="w-full flex flex-col items-center" data-testid="doc-preview-pdf">
                                {pdfRenderTimeout && pdfPageCount === 0 && (
                                    <div className="text-center px-6 py-8" data-testid="doc-preview-pdf-timeout">
                                        <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                                        <p className="text-sm text-zinc-600 mb-3">Preview taking too long. Click &quot;Open in full&quot; to view the document.</p>
                                        <Button variant="outline" className="rounded-none" onClick={handleOpenFull}>
                                            <ExternalLink className="w-4 h-4 mr-2" /> Open in full
                                        </Button>
                                    </div>
                                )}
                                <canvas
                                    ref={canvasRef}
                                    className="max-w-full h-auto bg-white shadow-sm border border-zinc-200"
                                    data-testid="doc-preview-pdf-canvas"
                                    style={{ display: pdfRenderTimeout && pdfPageCount === 0 ? 'none' : 'block' }}
                                />
                                {pdfPageCount > 1 && (
                                    <p className="text-xs text-zinc-500 mt-3" data-testid="doc-preview-pdf-pages">
                                        Page 1 of {pdfPageCount} — Open in full to view all pages
                                    </p>
                                )}
                                {pdfPageCount === 1 && (
                                    <p className="text-xs text-zinc-400 mt-3">Page 1 of 1</p>
                                )}
                            </div>
                        )}
                        {!loading && !error && blobUrl && kind === 'image' && (
                            <img
                                src={blobUrl}
                                alt={filename}
                                className="max-w-full max-h-full object-contain"
                                data-testid="doc-preview-image"
                            />
                        )}
                        {!loading && !error && blobUrl && kind === 'video' && (
                            <video
                                src={blobUrl}
                                controls
                                preload="metadata"
                                className="max-w-full max-h-full"
                                data-testid="doc-preview-video"
                            />
                        )}
                        {!loading && !error && blobUrl && kind === 'unknown' && (
                            <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
                                <FileText className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
                                <p className="text-sm text-zinc-600 mb-3">Inline preview not supported for this file type.</p>
                                <div className="flex gap-2 justify-center">
                                    <Button variant="outline" className="rounded-none" onClick={handleOpenFull}>
                                        <ExternalLink className="w-4 h-4 mr-2" /> Open in full
                                    </Button>
                                    <Button className="rounded-none bg-zinc-900 text-white" onClick={handleDownload}>
                                        <Download className="w-4 h-4 mr-2" /> Download
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex flex-col sm:flex-row gap-2 border-t border-zinc-200 px-4 py-3 flex-shrink-0">
                        <Button
                            variant="outline"
                            className="flex-1 rounded-none h-10 border-zinc-900 text-sm font-semibold"
                            onClick={handleOpenFull}
                            disabled={!blobUrl}
                            data-testid="doc-preview-open-full"
                        >
                            <ExternalLink className="w-4 h-4 mr-2" /> Open in full
                        </Button>
                        <Button
                            className="flex-1 rounded-none h-10 bg-zinc-900 text-white text-sm font-semibold"
                            onClick={handleDownload}
                            disabled={!blobUrl}
                            data-testid="doc-preview-download"
                        >
                            <Download className="w-4 h-4 mr-2" /> Download
                        </Button>
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
